import { FormLabel } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { ButtonWrapper, RanaButton } from "@nens/ranalib";

import { getDuplicateFileId } from "@/utils/get-duplicate-file-id";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { FileUploadingOverwriteModalProps } from "./file-uploading-overwrite-modal";

interface FileUploadingOverwriteModalFormProps extends FileUploadingOverwriteModalProps {
  activeStep: number;
  setActiveStep: (step: number) => void;
}

export function FileUploadingOverwriteModalForm(props: FileUploadingOverwriteModalFormProps) {
  const { overwriteState, setOverwrites, startOverwritesUpload, setActiveStep, activeStep } = props;

  const { overwrites, uploads } = overwriteState;

  return overwrites.map((overwrite, idx) => {
    if (idx !== activeStep) return null;

    const currentFiles = uploads.find(
      (upload) => upload.directory === overwrite.directory
    )?.currentFiles;
    if (!currentFiles) return null;
    const originalName = getNameFromFileId(currentFiles[overwrite.currentFileIndex].id);
    const duplicateName = getDuplicateFileId(
      originalName,
      currentFiles.map(({ id }) => id)
    );

    // Calling this with an empty object will reset it to its initial state
    const updateThisOverwrite = (
      update: Partial<Pick<typeof overwrite, "name" | "dontOverwrite">>
    ) => {
      const newOverwrites = overwrites.map((overwrite, innerIdx) =>
        innerIdx === idx
          ? { ...overwrite, name: originalName, dontOverwrite: false, ...update }
          : overwrite
      );

      if (idx === overwrites.length - 1) {
        startOverwritesUpload(newOverwrites);
      } else {
        setOverwrites(newOverwrites);
        setActiveStep(idx + 1);
      }
    };

    return (
      <ButtonWrapper w="fit" py={6} key={overwrite.currentFileIndex} flexDirection="column">
        <FormLabel textTransform="none">{overwrite.directory + overwrite.name}</FormLabel>
        <RanaButton
          variant="secondary"
          onClick={() => {
            updateThisOverwrite({ dontOverwrite: true });
          }}
        >
          <FormattedMessage id="button.dont_overwrite" defaultMessage="Do not overwrite" />
        </RanaButton>

        <RanaButton
          variant="secondary"
          onClick={() => {
            updateThisOverwrite({ name: duplicateName });
          }}
        >
          <FormattedMessage
            id="button.upload_as"
            defaultMessage="Upload as `{as}`"
            values={{ as: duplicateName }}
          />
        </RanaButton>
        <RanaButton
          variant="primary"
          onClick={() => {
            updateThisOverwrite({});
          }}
        >
          <FormattedMessage id="button.overwrite" defaultMessage="Overwrite" />
        </RanaButton>
      </ButtonWrapper>
    );
  });
}
